import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@ohif/ui';

import './StudyListPagination.css';

export interface StudyListPaginationProps {
  onChangePage: (page: number) => void;
  currentPage: number;
  perPage: number;
  onChangePerPage: (perPage: string | number) => void;
}

const RANGES = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

const StudyListPagination: React.FC<StudyListPaginationProps> = ({
  onChangePage,
  currentPage,
  perPage,
  onChangePerPage,
}) => {
  const { t } = useTranslation('StudyList');

  const navigateToPage = useCallback(
    (page: number) => {
      const toPage = page < 1 ? 1 : page;
      onChangePage(toPage);
    },
    [onChangePage]
  );

  const [selectedRange, setSelectedRange] = useState(
    () => RANGES.find(r => r.value === String(perPage)) ?? RANGES[0]
  );

  useEffect(() => {
    const range = RANGES.find(r => r.value === String(perPage));
    if (range && range.value !== selectedRange?.value) {
      setSelectedRange(range);
    }
  }, [perPage, selectedRange?.value]);

  const onSelectedRange = useCallback(
    (range: { value: string; label: string }) => {
      setSelectedRange(range);
      onChangePerPage(range.value);
    },
    [onChangePerPage]
  );

  return (
    <div className="study-list-pagination-container">
      <div className="study-list-pagination-accent-line" />

      <div className="flex items-center justify-between px-3 py-4">
        <div className="flex items-center gap-3" id="rows-per-page-wrapper">
          <span className="text-xs font-medium uppercase tracking-wider study-list-pagination-label">
            {t('Results per page')}
          </span>
          <div className="flex gap-1">
            {RANGES.map(range => {
              const isActive = selectedRange?.value === range.value;
              return (
                <button
                  key={range.value}
                  onClick={() => onSelectedRange(range)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 study-list-pagination-range-btn ${
                    isActive ? 'active' : ''
                  }`}
                >
                  {range.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg study-list-pagination-page-badge">
            <span className="text-xs font-medium study-list-pagination-page-label">
              {t('Page')}
            </span>
            <span className="text-sm font-bold px-2 py-0.5 rounded-md study-list-pagination-page-number">
              {currentPage}
            </span>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => navigateToPage(1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 study-list-pagination-nav-btn"
              title={t('First page')}
              aria-label={t('First page')}
            >
              <Icon name="arrow-left-double" />
            </button>

            <button
              onClick={() => navigateToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium transition-all duration-200 study-list-pagination-nav-btn"
              aria-label={t('Previous')}
            >
              <Icon name="arrow-left-small" />
              {t('Previous')}
            </button>

            <button
              onClick={() => navigateToPage(currentPage + 1)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold transition-all duration-200 study-list-pagination-next-btn"
              aria-label={t('Next')}
            >
              {t('Next')}
              <Icon name="arrow-right-small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

StudyListPagination.propTypes = {
  onChangePage: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  onChangePerPage: PropTypes.func.isRequired,
};

const MemoizedStudyListPagination = memo(StudyListPagination);

export default MemoizedStudyListPagination;
export { MemoizedStudyListPagination as StudyListPagination };
